import { profileAPI } from '../api';
import {actionSetProfile, actionLogout, selectIsAuth} from '@store/profile.reducer';
import store from '@store/redux.store';
import jwt_decode from 'jwt-decode';
import axios from 'axios';
import {unsubscribeFcmMessaging} from "@hooks/useFcmMessaging";
import clearLocalStorageExcept from '@/helpers/clearLocalStorageExcept';
import { getApiErrorMessage } from '@/helpers/getApiErrorMessage';
import { PLATFORM_TRAINING_BANNER_STORAGE_KEY } from '@/helpers/platformTraining';
import websocket from '@/utils/websocket';

export const idUserAuthorizedOnTheAuthorizationPage = 'idUserAuthorizedOnTheAuthorizationPage';

const saveGamificationRewards = (data) => {
	const dailyLoginReward = data?.daily_login_reward
	const achievementReward = data?.achievement_reward

	if (dailyLoginReward) {
		localStorage.setItem('dailyLoginReward', JSON.stringify(dailyLoginReward))
	}

	if (achievementReward) {
		localStorage.setItem('achievementReward', JSON.stringify(achievementReward))
	}

	if (dailyLoginReward || achievementReward) {
		window.dispatchEvent(new Event('gamificationRewardUpdated'))
	}
}

const isTwoFactorRequired = (data) => {
	return data?.two_factor_required
		|| data?.requires_2fa
		|| data?.twoFactorRequired
		|| data?.need_2fa
		|| data?.needTwoFactor
		|| data?.requiresTwoFactor
		|| data?.status === 'two_factor_required';
}

const getTwoFactorChallenge = (data, email) => ({
	token: data?.two_factor_token || data?.twoFactorToken || data?.verification_token || data?.challenge_token,
	uuid: data?.uuid || data?.challenge_id || data?.challengeId,
	email: data?.email || email,
})

let pendingTwoFactorLogin = null;

const decodeAccessToken = (token) => {
	if (typeof token !== 'string') return null;

	const normalizedToken = token.trim();
	if (!normalizedToken || normalizedToken === 'undefined' || normalizedToken === 'null') {
		return null;
	}

	try {
		const payload = jwt_decode(normalizedToken);
		return payload && typeof payload === 'object'
			? { token: normalizedToken, payload }
			: null;
	} catch (error) {
		return null;
	}
};

const saveAccessToken = (token) => {
	const accessToken = decodeAccessToken(token);

	if (!accessToken) {
		localStorage.removeItem('userToken');
		return null;
	}

	localStorage.setItem('userToken', accessToken.token);
	return accessToken;
};

export const AuthService = {
	auth: (user, token = null, refresh_token = null , saveParentId = false) => {
		const accessToken = saveAccessToken(token);
		if (!accessToken) return false;

		if (user.roles?.length > 0) {
			user.roles = user.roles?.map((i) => i?.item_name);
		}

		user.role = accessToken.payload?.role;

		if(saveParentId && accessToken.payload?.uid){
			localStorage.setItem(idUserAuthorizedOnTheAuthorizationPage, accessToken.payload.uid)
		}

		// localStorage.setItem('userRefreshToken', refresh_token);
		if(refresh_token){
			localStorage.setItem('userRefreshToken', refresh_token);
		}
		
		store.dispatch(actionSetProfile(user));
		return true;
	},

	login: async (email, password) => {
		try {
			const result = await profileAPI.login({ email, password });

			if (result?.status === 200) {
				if (isTwoFactorRequired(result?.data)) {
					return {
						status: 'two_factor_required',
						challenge: getTwoFactorChallenge(result.data, email),
						message: result.data?.message,
					};
				}

				const user  = result?.data?.user
				const token = result.data.token
				const refresh_token = result.data?.refresh_token

				if (user) {
					if (store.getState()?.settings?.twoFactorAuthEnabled == 1) {
						pendingTwoFactorLogin = {
							user,
							token,
							refresh_token,
							data: result.data,
						}

						return {
							status: 'two_factor_required',
							challenge: {
								email,
								mock: true,
							},
						};
					}

					if (!AuthService.auth(user, token, refresh_token, true)) {
						return {
							status: 'error',
							message: 'Сервер не вернул корректный токен авторизации.',
							user: null,
						};
					}
					saveGamificationRewards(result.data)

					return { status: 'ok', user: user };
				}
			}

			if (!result?.response) {
				return {
					status: 'network_error',
					message: 'Нет соединения с сервером. Проверьте интернет и попробуйте еще раз.',
				};
			}

			const responseError = getApiErrorMessage(result, result.response?.data?.message);
			const responseCode = result.response?.data?.meta?.code;

			if (result.response?.status === 401 || responseCode === 'invalid_credentials') {
				return {
					status: 'invalid_credentials',
					message: responseError || 'Неверная почта/телефон или пароль',
				};
			}

			return {
				status: 'error',
				message: responseError || 'Не удалось выполнить вход. Попробуйте позже.',
				user: null,
			};
		} catch (error) {
			if (!error?.response) {
				return {
					status: 'network_error',
					message: 'Нет соединения с сервером. Проверьте интернет и попробуйте еще раз.',
				};
			}

			return {
				status: 'error',
				message: getApiErrorMessage(error, error.response?.data?.message || 'Не удалось выполнить вход. Попробуйте позже.'),
				user: null,
			};
		}
	},

	confirmTwoFactorLogin: async (data) => {
		if (pendingTwoFactorLogin) {
			if (data?.code !== '1234') {
				return {
					status: 'error',
					message: 'Неверный код подтверждения.',
				};
			}

			const { user, token, refresh_token, data: loginData } = pendingTwoFactorLogin;
			pendingTwoFactorLogin = null;

			if (!AuthService.auth(user, token, refresh_token, true)) {
				return {
					status: 'error',
					message: 'Сервер не вернул корректный токен авторизации.',
				};
			}
			saveGamificationRewards(loginData);

			return { status: 'ok', user };
		}

		try {
			const result = await profileAPI.confirmTwoFactorLogin(data);

			if (result?.status === 200 && result?.data?.user) {
				const user = result.data.user;
				const token = result.data.token;
				const refresh_token = result.data?.refresh_token;

				if (!AuthService.auth(user, token, refresh_token, true)) {
					return {
						status: 'error',
						message: 'Сервер не вернул корректный токен авторизации.',
					};
				}
				saveGamificationRewards(result.data);

				return { status: 'ok', user };
			}

			const responseError = getApiErrorMessage(result, result?.response?.data?.message);

			return {
				status: 'error',
				message: responseError || 'Не удалось подтвердить код. Попробуйте еще раз.',
			};
		} catch (error) {
			return {
				status: 'error',
				message: getApiErrorMessage(error, error.response?.data?.message || 'Не удалось подтвердить код. Попробуйте еще раз.'),
			};
		}
	},

	appLogin: async (app_id, uuid) => {
		return await profileAPI.appLogin({ app_id, uuid }).then((result) => {
			if (result?.status === 200) {
				if (result?.data?.user) {
					const data = { ...result.data.user };
					if (result?.data?.user?.roles.length > 0)
						data.roles = result?.data?.user?.roles?.map((i) => i?.item_name);
					const accessToken = saveAccessToken(result?.data?.token);
					if (!accessToken) return { status: 'error' };
					data.role = accessToken.payload?.role;
					saveGamificationRewards(result.data)
					store.dispatch(actionSetProfile(data));
					return { status: 'ok' };
				}
			}
			return { status: 'error' };
		});
	},

	logout: async () => {
		websocket.disconnect();
		unsubscribeFcmMessaging();
		clearLocalStorageExcept([
			'hideAdminSupportHelper',
			'manualsModalSeen',
			PLATFORM_TRAINING_BANNER_STORAGE_KEY,
		])
		store.dispatch(actionLogout());
	},

	refreshToken: async () =>
		await axios
			.post( 
				`${process.env.REACT_APP_URL_API}auth/refresh-token`,
				{
					refresh_token: localStorage.getItem('userRefreshToken'),
					...(store?.getState()?.profile?.profile?.role ? {role: store.getState()?.profile?.profile?.role} : {})
				},
				{ withCredentials: true }
			)
			.then((res) => { 
				if (res.data?.statusCode === 401) {
					throw new Error(res.data?.message || 'Refresh token is no longer valid');
				}

				const accessToken = saveAccessToken(res.data?.token);
				if (!accessToken) {
					throw new Error('Refresh response does not contain a valid access token');
				}

				if (res.data?.refresh_token){
					localStorage.setItem('userRefreshToken', res.data.refresh_token);
				}

				return accessToken.token;
			}),
};
